import type { Column } from '@tanstack/react-table'

export const sanitizeColumnId = (id: string) =>
  id.toLowerCase().replace(/\s+/g, '-')

// Pinned columns are sticky on horizontal scroll by default;
// meta.enableSticky === false opts a pinned column out, keeping its fixed
// order and drag lock without sticking (e.g. Artist/Source vs select/Title).
// meta is read via an explicit shape: its augmentation lives in frontend's
// tanstack-table.d.ts, which the cl strict program doesn't compile.
export const isStickyPinned = <T>(column: Column<T>): boolean => {
  const meta = column.columnDef.meta as { enableSticky?: boolean } | undefined
  return !!column.getIsPinned() && meta?.enableSticky !== false
}

// Inserts columnId after anchorId in a saved order that predates it.
export const insertColumnAfter = (
  order: string[],
  columnId: string,
  anchorId: string,
): string[] => {
  if (order.length === 0 || order.includes(columnId)) return order
  const anchorIndex = order.indexOf(anchorId)
  if (anchorIndex === -1) return [...order, columnId]
  return [
    ...order.slice(0, anchorIndex + 1),
    columnId,
    ...order.slice(anchorIndex + 1),
  ]
}

// Reconciles a persisted column order against the table's live leaf columns:
export const reconcileColumnOrder = (
  savedOrder: string[],
  leafIds: string[],
): string[] => {
  const kept = savedOrder.filter(id => leafIds.includes(id))
  return [...kept, ...leafIds.filter(id => !kept.includes(id))]
}

// Moves draggedId to targetId's position within currentOrder. Returns the new
// order, or null when the move is a no-op (either id missing, or same position).
export const reorderColumns = (
  currentOrder: string[],
  draggedId: string,
  targetId: string,
): string[] | null => {
  const from = currentOrder.indexOf(draggedId)
  const to = currentOrder.indexOf(targetId)
  if (from === -1 || to === -1 || from === to) return null

  const next = [...currentOrder]
  next.splice(from, 1)
  next.splice(to, 0, draggedId)
  return next
}
